import React, { useState, useRef } from 'react';
import { ConsentNotice } from '@/components/ConsentNotice';
import axios from 'axios';
import { Checkbox } from '@/components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { motion } from 'framer-motion';
import Webcam from 'react-webcam';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X, Camera, Upload, Utensils } from 'lucide-react';
import { uploadToCloudinary, createFoodEntry, uploadRejectionReason, UPLOAD_REJECT_MESSAGE } from '@/utils/api';
import { toast } from 'sonner';

const FoodUploadModal = ({ onClose, onSuccess, mealType = 'meal' }) => {
  const { token } = useAuth();
  const [needsUploadConsent, setNeedsUploadConsent] = useState(() => {
    try { return localStorage.getItem('insul_upload_consent_done') !== 'true'; } catch { return true; }
  });
  const [uploadConsentAi, setUploadConsentAi] = useState(false);
  const [step, setStep] = useState('choose');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  const getMealTypeLabel = () => {
    if (mealType === 'snack') return 'Snack';
    if (mealType === 'drink') return 'Drink';
    return 'Meal';
  };

  const getMealTypeIcon = () => {
    if (mealType === 'snack') return <Utensils className="w-5 h-5 text-secondary" />;
    if (mealType === 'drink') return <Utensils className="w-5 h-5 text-primary" />;
    return <Utensils className="w-5 h-5 text-primary" />;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const rejection = uploadRejectionReason(file);
      if (rejection) {
        toast.error(rejection, { id: 'upload-reject' });
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setStep('preview');
    }
  };

  const handleCapture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    fetch(imageSrc)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
        setSelectedFile(file);
        setPreview(imageSrc);
        setStep('preview');
      });
  };

  const handleUpload = async () => {
    if (needsUploadConsent) {
      if (!uploadConsentAi) {
        toast.error('Please consent to AI processing to upload meal photos.');
        return;
      }
      try {
        // Director 16 Oct 2026: the optional research/"improve the food database" checkbox is hidden; research_use_consent is NOT sent
        // from this screen any more, so existing research_use_consent records are left exactly as they are.
        await axios.post(`${API_URL}/api/auth/me/consents`, {
          ai_processing_consent: true,
          screen: 'food_upload',
          notice_key: 'image_upload'
        }, { headers: { Authorization: `Bearer ${token}` } });
        localStorage.setItem('insul_upload_consent_done', 'true');
        setNeedsUploadConsent(false);
      } catch (e) { /* recorded next time if this fails */ }
    }
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      // Step 1: Upload to Cloudinary
      toast.loading('Uploading image...', { id: 'upload' });
      let cloudinaryResponse;
      try {
        cloudinaryResponse = await uploadToCloudinary(selectedFile, token);
        if (!cloudinaryResponse || !cloudinaryResponse.secure_url || !cloudinaryResponse.public_id) {
          throw new Error('Cloudinary upload failed - no URL returned');
        }
      } catch (cloudError) {
        console.error('Cloudinary upload error:', cloudError);
        toast.error('Failed to upload image. Please check your internet connection and try again.', { id: 'upload' });
        setUploading(false);
        return;
      }
      
      // Step 2: Create food entry in database
      toast.loading('Saving to your food diary...', { id: 'upload' });
      try {
        const entry = await createFoodEntry(
          cloudinaryResponse.secure_url,
          cloudinaryResponse.public_id,
          notes,
          mealType,
          token,
          cloudinaryResponse.format
        );
        
        if (!entry || !entry.id) {
          throw new Error('Database save failed - no entry ID returned');
        }
        
        if (entry.analysis_error === 'ai_unavailable') {
          toast.success(`${getMealTypeLabel()} saved. Analysis temporarily unavailable, please try again shortly from the entry.`, { id: 'upload', duration: 8000 });
        } else {
          toast.success(`${getMealTypeLabel()} uploaded successfully!`, { id: 'upload' });
        }
        onSuccess();
      } catch (dbError) {
        console.error('Database save error:', dbError);
        toast.error('Image uploaded but failed to save to diary. Please try again or contact support.', { id: 'upload', duration: 5000 });
        // Image is in Cloudinary but not in DB - this is a data integrity issue
        // Log this for debugging
        console.error('DATA INTEGRITY WARNING: Image uploaded to Cloudinary but not saved to DB', {
          cloudinary_url: cloudinaryResponse.secure_url,
          public_id: cloudinaryResponse.public_id,
          error: dbError.message
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error?.response?.data?.detail;
      const rejected = error?.message === UPLOAD_REJECT_MESSAGE || detail === UPLOAD_REJECT_MESSAGE || /allowed_formats|not allowed|File size too large/i.test(error?.message || '');
      toast.error(rejected ? UPLOAD_REJECT_MESSAGE : `Failed to upload ${getMealTypeLabel().toLowerCase()}. Please try again.`, { id: 'upload' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="ph-no-capture fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-card rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border p-6 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Add {getMealTypeLabel()}</h2>
          <button
            data-testid="close-upload-modal"
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 'choose' && (
            <div className="space-y-6">
              <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
                <div className="flex items-start gap-3 mb-3">
                  {getMealTypeIcon()}
                  <div>
                    <h3 className="font-semibold mb-1">
                      {mealType === 'drink' ? 'Capture Your Drink' : 'Size Reference Tip'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {mealType === 'drink' 
                        ? 'Take a clear photo of your beverage'
                        : 'Include a spoon or fork in your photo to help estimate size'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  data-testid="use-camera-button"
                  onClick={() => setStep('camera')}
                  className="bg-card text-card-foreground rounded-2xl border-2 border-border hover:border-primary shadow-sm hover:shadow-md transition-all p-8 space-y-4 group"
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
                    <Camera className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Use Camera</h3>
                    <p className="text-sm text-muted-foreground">Take a photo now</p>
                  </div>
                </button>

                <button
                  data-testid="upload-file-button"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-card text-card-foreground rounded-2xl border-2 border-border hover:border-primary shadow-sm hover:shadow-md transition-all p-8 space-y-4 group"
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-secondary/10 group-hover:bg-secondary/20 flex items-center justify-center transition-colors">
                    <Upload className="w-8 h-8 text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Upload Photo</h3>
                    <p className="text-sm text-muted-foreground">Choose from gallery</p>
                  </div>
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {step === 'camera' && (
            <div className="space-y-6">
              <div className="relative rounded-xl overflow-hidden bg-black">
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  className="w-full"
                  videoConstraints={{ facingMode: 'environment' }}
                />
                  <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-4 left-4 right-4 p-3 bg-black/60 backdrop-blur-sm rounded-lg text-white text-sm flex items-center gap-2">
                    {getMealTypeIcon()}
                    {mealType === 'drink' 
                      ? 'Capture your beverage' 
                      : 'Include utensils for size reference'}
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setStep('choose')}
                  className="flex-1 rounded-full px-6 py-6 text-lg font-semibold"
                >
                  Back
                </Button>
                <Button
                  data-testid="capture-photo-button"
                  onClick={handleCapture}
                  className="flex-1 rounded-full px-6 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-primary to-[#8FD14F]"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Capture
                </Button>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-6">
              <div className="rounded-xl overflow-hidden border border-border">
                <img src={preview} alt="Preview" className="w-full" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="meal-notes-input"
                  placeholder="Add any notes about this meal..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="rounded-xl border-border bg-background/50 focus:ring-2 focus:ring-ring focus:border-transparent px-4 py-3 shadow-sm transition-all min-h-24"
                />
              </div>
              {needsUploadConsent && (
                <div className="rounded-xl border border-border/60 bg-background/50 p-4 space-y-3" data-testid="upload-consent-section">
                  <p className="text-xs text-muted-foreground">
                    <ConsentNotice slot="image_upload">Collection notice: your meal photo and notes are analyzed by an AI service to estimate
                    nutrition. Processing occurs on overseas servers. Photos are automatically deleted after 7 days.</ConsentNotice>
                  </p>
                  <div className="flex items-start gap-3">
                    <Checkbox id="upload-ai-consent" data-testid="upload-ai-consent-checkbox" checked={uploadConsentAi}
                      onCheckedChange={(c) => setUploadConsentAi(c === true)} className="mt-0.5" />
                    <label htmlFor="upload-ai-consent" className="text-sm leading-snug cursor-pointer">
                      I consent to my meal photos being processed by AI, including on overseas servers (required)
                    </label>
                  </div>
                </div>
              )}
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep('choose');
                    setPreview(null);
                    setSelectedFile(null);
                    setNotes('');
                  }}
                  className="flex-1 rounded-full px-6 py-6 text-lg font-semibold"
                  disabled={uploading}
                >
                  Retake
                </Button>
                <Button
                  data-testid="save-meal-button"
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 rounded-full px-6 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-primary to-[#8FD14F]"
                >
                  {uploading ? 'Saving...' : `Save ${getMealTypeLabel()}`}
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FoodUploadModal;